<script setup lang="ts">
  // Payment-method sales stats migrated to the V2 design system.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import StatsService from '@/api/services/stats.service';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsIntervalSelect from '@/components/v2/stats/V2StatsIntervalSelect.vue';
  import { TStatsChartInterval } from '@/components/v2/stats/v2-stats-interval.types';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import V2StatsSeriesReport from '@/components/v2/stats/V2StatsSeriesReport.vue';
  import { useStatsSeriesDisplay } from '@/composition/use-stats-series-display';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { OrderType } from '@/enums/OrderEnums';
  import { IStats } from '@/interfaces/IStats';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { buildChartLabels } from '@/statsutils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? ''
  );

  const isWhatsappMode = computed(
    () => restaurantStore.selectedBranch?.ordersMode === 'wa'
  );

  //#region Filters

  const range = ref<IStatsDateRange | null>(null);
  const interval = ref<TStatsChartInterval>('auto');

  const orderTypeOptions = computed(() => [
    { id: OrderType.Seat, label: String(proxy.$tc('order.type.seat')) },
    { id: OrderType.Away, label: String(proxy.$tc('order.type.away')) },
    { id: OrderType.Delivery, label: String(proxy.$tc('order.type.delivery')) },
  ]);

  const selectedOrderTypes = ref<string[]>([
    OrderType.Seat,
    OrderType.Away,
    OrderType.Delivery,
  ]);

  function toggleOrderType(id: string) {
    const current = selectedOrderTypes.value;
    if (current.includes(id)) {
      if (current.length === 1) return;
      selectedOrderTypes.value = current.filter((t) => t !== id);
    } else {
      selectedOrderTypes.value = [...current, id];
    }
  }

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  watch([interval, selectedOrderTypes], () => {
    load();
  });

  //#endregion

  //#region Loading

  const statsData = ref<IStats | null>(null);
  const loading = ref(false);

  const SERIES_LABEL_KEYS: Record<string, string> = {
    count: 'v2Stats.series.count',
    total: 'v2Stats.series.total',
    cash: 'v2Stats.series.cash',
    card: 'v2Stats.series.card',
    online: 'v2Stats.series.online',
    banktransfer: 'v2Stats.series.banktransfer',
    qr: 'v2Stats.series.qr',
    invitation: 'v2Stats.series.invitation',
    employee: 'v2Stats.series.employee',
    customer: 'v2Stats.series.customerAccount',
    club: 'v2Stats.bucket.club',
  };

  const SERIES_COLORS = [
    '#45a5f5',
    '#30d565',
    '#f5e938',
    '#f56041',
    '#f5418a',
    '#c44ff5',
    '#6451f5',
    '#00b8b8',
  ];

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value || isWhatsappMode.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const stats = await StatsService.getPaymentMethodsStats(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        selectedOrderTypes.value,
        interval.value,
        undefined,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;

      stats.labels = buildChartLabels(stats);
      statsData.value = stats;
    } catch (error) {
      if (generation !== requestGeneration) return;
      statsData.value = null;
      ui.serverError(error, i18nT('v2Stats.paymentMethods.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const displayStats = useStatsSeriesDisplay({
    stats: statsData,
    labelKeys: SERIES_LABEL_KEYS,
    colors: SERIES_COLORS,
    currencySymbol,
  });

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.paymentMethods.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsIntervalSelect :value="interval" @input="interval = $event" />
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="paymentMethods"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        v-if="!isWhatsappMode"
        storage-key="paymentMethods"
        :text="String($t('statsNote.paymentMethods'))"
      />
    </template>

    <div v-if="isWhatsappMode" class="wa-guard">
      <PosUiEmptyState
        icon="whatsapp"
        :title="String($t('v2Stats.waModeTitle'))"
        :description="String($t('v2Stats.waModeText'))"
      />
    </div>

    <template v-else>
      <div class="type-filter">
        <button
          v-for="option in orderTypeOptions"
          :key="option.id"
          type="button"
          class="type-chip"
          :class="{ 'is-active': selectedOrderTypes.includes(option.id) }"
          @click="toggleOrderType(option.id)"
        >
          {{ option.label }}
        </button>
      </div>

      <V2StatsSeriesReport
        :stats="displayStats"
        :loading="loading"
        chart-type="line"
        :chart-title="String($t('v2Stats.paymentMethods.chartTitle'))"
        :currency-symbol="currencySymbol"
        export-filename="payment-methods-stats"
        :export-sheet="String($t('v2Stats.paymentMethods.title'))"
        :first-column-label="String($t('v2Stats.colDate'))"
      />
    </template>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .wa-guard {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
  }

  .type-filter {
    @apply tw-flex tw-flex-wrap tw-items-center;

    .type-chip {
      // margins instead of flex gap: unsupported on Chrome <84.
      @apply tw-mb-1 tw-mr-1.5 tw-h-11 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
